import { ByzzerMask } from '@/components/ByzzerMask/ByzzerMask';
import { openModal } from '@/components/form';
import { ReportRunConfig } from '@/types/ReportRun';
import { ByzzerTextInput, ByzzerTipIcon } from '@byzzer/ui-components';
import './ScheduleModal.scss';
import { CreateReportSchedulePayload } from '@/types/ApiTypes';
import { useCallback } from 'react';
import { debounce } from 'lodash';

const baseClassName = 'schedule-report-run-now-modal';
interface CreateReportScheduleModalParams {
    runConfig: ReportRunConfig;
    schedule: Schedule;
    seriesName: string;
    sku: string;
    createScheduleReport: Function;
    checkDuplicateSeries: Function;
}

interface ModifySeriesNameModalParams {
    seriesId: number | null;
    reportRunId?: number;
    seriesName: string | null;
    checkDuplicateSeries: Function;
    updateScheduledReportSeriesName: Function;
    updateSeriesNameForReportRun: Function;
    isScheduledReport: boolean;
}

interface ModifyReportReScheduleModalParams {
    scheduleId: number;
    scheduleInfo: Schedule;
    rescheduleReport: Function;
}

interface ModifyReportScheduleConfigModalParams {
    seriesId: number;
    runConfig: ReportRunConfig;
    updateScheduledReportConfig: Function;
}

export async function openCreateReportScheduleModal({
    runConfig,
    sku,
    schedule,
    createScheduleReport,
    checkDuplicateSeries,
    seriesName
}: CreateReportScheduleModalParams) {
    const duplicateCheckRef = { checked: false, isDuplicate: false };

    return openModal<Omit<CreateReportScheduleModalParams, 'createScheduleReport' | 'checkDuplicateSeries'>>({
        className: baseClassName,
        title: 'Create Report Schedule',
        initialState: {
            seriesName,
            runConfig,
            schedule,
            sku,
        },
        showCloseOption: false,
        content: ({ busy, state, setState }) => {
            function handleDisplayNameChange(e: ByzzerChangeEvent<string>) {
                setState((state) => ({
                    ...state,
                    seriesName: e.value,
                }));
                // Reset duplicate check when name changes
                duplicateCheckRef.checked = false;
                duplicateCheckRef.isDuplicate = false;
            }

            return (
                <>
                    <ByzzerMask loading={busy}>Creating Your Schedule</ByzzerMask>
                    <ByzzerTextInput
                        className={`${baseClassName}__name`}
                        name={'name'}
                        placeholder={'Enter a title'}
                        label={
                            <>
                                Name your Report series{' '}
                                <ByzzerTipIcon>
                                    The series name will allow you to quickly find all runs in the same series or setup.
                                    Each run will have the week ending date added to differentiate each run in the
                                    series.
                                </ByzzerTipIcon>
                            </>
                        }
                        value={state.seriesName}
                        onChange={handleDisplayNameChange}
                    />
                    {duplicateCheckRef.isDuplicate && (
                        <span className={`${baseClassName}__error`}>
                            You already have a series with this name. Please choose a different series name.
                        </span>
                    )}
                </>
            );
        },
        actions: [
            {
                key: 'cancel',
                type: 'negative',
                label: 'Cancel',
                disableIf({ busy }) {
                    return busy;
                },
                action({ resolve }) {
                    resolve();
                },
            },
            {
                key: 'create',
                label: 'Create',
                getDisableTip({ state }) {
                    if (duplicateCheckRef.checked && duplicateCheckRef.isDuplicate) {
                        return 'You already have a series with this name. Please choose a different series name.';
                    }

                    return '';
                },
                disableIf({ busy, state }) {
                    return busy || !state.seriesName || (duplicateCheckRef.checked && duplicateCheckRef.isDuplicate);
                },
                async action({ resolve, reject, setBusy, state }) {
                    try {
                        setBusy(true);

                        if (!duplicateCheckRef.checked) {
                            const isDuplicate = await checkDuplicateSeries({ name: state.seriesName });
                            
                            duplicateCheckRef.checked = true;
                            duplicateCheckRef.isDuplicate = isDuplicate.exists;

                            if (isDuplicate.exists) {
                                return;
                            }
                        }

                        const payload: CreateReportSchedulePayload = {
                            seriesName: state.seriesName,
                            runConfig: state.runConfig,
                            sku: state.sku,
                            scheduleInfo: {
                                frequency: state.schedule.frequency,
                                deliveryDay: state.schedule.deliveryDay,
                                deliveryGroupType: state.schedule.deliveryGroupType,
                                deliverToUserIds: state.schedule.deliverToUserIds,
                                startingTimePeriod: state.schedule.startingTimePeriod,
                                timePeriods: state.schedule.timePeriods,
                                deliveryCount: state.schedule.duration,
                                scheduleDates: state.schedule.scheduleDates?.map((dt) => ({
                                    reportWeekEnding: dt.endDate,
                                    reportDeliverAt: dt.deliveryDate,
                                })),
                            },
                        };

                        resolve(await createScheduleReport(payload));
                    } catch (err) {
                        reject(err);
                    } finally {
                        setBusy(false);
                    }
                },
            },
        ],
    });
}

export async function openRenameReportSeriesName({
    seriesId,
    reportRunId,
    seriesName,
    checkDuplicateSeries,
    updateScheduledReportSeriesName,
    updateSeriesNameForReportRun,
    isScheduledReport,
}: ModifySeriesNameModalParams) {
    return openModal({
        className: baseClassName,
        title: (
            <div className={`${baseClassName}__templatize`}>
                Rename across the entire series{' '}
                <ByzzerTipIcon>
                    You are editing the name of a scheduled run. Because scheduled runs are a concatenation of the
                    series name and the week ending date, you will be updating the run names across the entire scheduled
                    series.
                </ByzzerTipIcon>
            </div>
        ),
        initialState: {
            seriesId,
            reportRunId,
            seriesName: seriesName,
            oldSeriesName: seriesName,
            validatingSeriesName: false,
            isDuplicateSeriesName: false
        },
        showCloseOption: false,
        content: ({ busy, state, setState, setBusy }) => {
            const debounceCheckDuplicates = useCallback(
                debounce(async (seriesName: string) => {
                    if (!seriesName) return;
    
                    setState(prev => ({ ...prev, validatingSeriesName: true }));

                    try {
                        const isDuplicate = await checkDuplicateSeries({ 
                            name: seriesName,
                            type: isScheduledReport ? '' : 'scheduled'
                        });

                        setState(prev => ({
                            ...prev,
                            validatingSeriesName: false,
                            isDuplicateSeriesName: isDuplicate.exists
                        }));

                    } catch (error) {
                        console.error('Error checking duplicate series name:', error);
                    } finally {
                        setState(prev => ({ ...prev, validatingSeriesName: false }));
                    }
                }, 500),
                []
            )

            function handleDisplayNameChange(e: ByzzerChangeEvent<string>) {                
                setState(prev => ({
                    ...prev,
                    seriesName: e.value,
                    isDuplicateSeriesName: false
                }));
                debounceCheckDuplicates(e.value?.trim());
            }

            return (
                <>
                    <ByzzerMask loading={busy && !state.validatingSeriesName}>Renaming your report</ByzzerMask>
                    <ByzzerTextInput
                        className={`${baseClassName}__name`}
                        name={'name'}
                        placeholder={'Enter a title'}
                        value={state.seriesName}
                        onChange={handleDisplayNameChange}
                    />
                    {/* {state.isDuplicateSeriesName && (
                        <span className={`${baseClassName}__error`}>
                            You already have a series with this name. Please choose a different series name.
                        </span>
                    )} */}
                </>
            );
        },
        actions: [
            {
                key: 'cancel',
                type: 'negative',
                label: 'Cancel',
                disableIf({ busy }) {
                    return busy;
                },
                action({ resolve }) {
                    resolve();
                },
            },
            {
                key: 'update',
                label: 'Update',
                getDisableTip({ state: { seriesName, oldSeriesName, validatingSeriesName, isDuplicateSeriesName } }) {
                    if (!seriesName) {
                        return 'Series Name is required';
                    }

                    if (seriesName === oldSeriesName) {
                        return 'New series name is required.'
                    }

                    if (validatingSeriesName) {
                        console.log("Innnn here...")
                        return 'Validating series name.';
                    }

                    if (isDuplicateSeriesName) {
                        return 'You already have a series with this name. Please choose a different series name.';
                    }

                    return '';
                },
                disableIf({ busy, state: { seriesName, oldSeriesName, validatingSeriesName, isDuplicateSeriesName } }) {
                    return busy || !seriesName?.trim() || validatingSeriesName || isDuplicateSeriesName || oldSeriesName === seriesName;
                },
                async action({ resolve, reject, setBusy, state }) {
                    try {
                        setBusy(true);
                        const trimmedSeriesName = state.seriesName?.trim();

                        if (trimmedSeriesName) {
                            let response: any;

                            if (state.seriesId) {
                                response = await updateScheduledReportSeriesName(state.seriesId, trimmedSeriesName);
                            } else {
                                response = await updateSeriesNameForReportRun(state.reportRunId, trimmedSeriesName);
                            }

                            if (response) {
                                resolve(trimmedSeriesName);
                            } else {
                                reject('unable to update');
                            }
                        } else {
                            reject('Series Name is required.');
                        }
                    } catch (err) {
                        reject(err);
                    } finally {
                        setBusy(false);
                    }
                },
            },
        ],
    });
}

export async function openModifyReportRescheduleModal({
    scheduleId,
    scheduleInfo,
    rescheduleReport,
}: ModifyReportReScheduleModalParams) {
    return openModal<Omit<ModifyReportReScheduleModalParams, 'rescheduleReport'>>({
        className: baseClassName,
        initialState: { scheduleId, scheduleInfo },
        showCloseOption: false,
        content: ({ busy }) => (
            <>
                <ByzzerMask loading={busy}>Updating Your Schedule</ByzzerMask>
                <p>Are you sure you want to modify all scheduled runs for this series?</p>
            </>
        ),
        actions: [
            {
                key: 'no',
                type: 'negative',
                label: 'No',
                disableIf({ busy }) {
                    return busy;
                },
                action({ resolve }) {
                    resolve();
                },
            },
            {
                key: 'yes',
                label: 'Yes',
                disableIf({ busy }) {
                    return busy;
                },
                async action({ resolve, reject, setBusy, state }) {
                    try {
                        setBusy(true);

                        const scheduleInfo = {
                            frequency: state.scheduleInfo.frequency,
                            deliveryDay: state.scheduleInfo.deliveryDay,
                            deliveryGroupType: state.scheduleInfo.deliveryGroupType,
                            deliverToUserIds: state.scheduleInfo.deliverToUserIds,
                            startingTimePeriod: state.scheduleInfo.startingTimePeriod,
                            timePeriods: state.scheduleInfo.timePeriods,
                            deliveryCount: state.scheduleInfo.duration,
                            scheduleDates: state.scheduleInfo.scheduleDates?.map((dt) => ({
                                reportWeekEnding: dt.endDate,
                                reportDeliverAt: dt.deliveryDate,
                            })),
                        };

                        resolve(await rescheduleReport(state.scheduleId, scheduleInfo));
                    } catch (err) {
                        reject(err);
                    } finally {
                        setBusy(false);
                    }
                },
            },
        ],
    });
}

export async function openModifyReportScheduleConfigModal({
    seriesId,
    runConfig,
    updateScheduledReportConfig,
}: ModifyReportScheduleConfigModalParams) {
    return openModal<Omit<ModifyReportScheduleConfigModalParams, 'updateScheduledReportConfig'>>({
        className: baseClassName,
        initialState: { seriesId, runConfig },
        showCloseOption: false,
        content: ({ busy }) => (
            <>
                <ByzzerMask loading={busy}>Updating Your Schedule</ByzzerMask>
                <p>Are you sure you want to modify all scheduled runs for this series?</p>
            </>
        ),
        actions: [
            {
                key: 'no',
                type: 'negative',
                label: 'No',
                disableIf({ busy }) {
                    return busy;
                },
                action({ resolve }) {
                    resolve();
                },
            },
            {
                key: 'yes',
                label: 'Yes',
                disableIf({ busy }) {
                    return busy;
                },
                async action({ resolve, reject, setBusy, state }) {
                    try {
                        setBusy(true);
                        resolve(await updateScheduledReportConfig(state.seriesId, state.runConfig));
                    } catch (err) {
                        reject(err);
                    } finally {
                        setBusy(false);
                    }
                },
            },
        ],
    });
}
